import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import ChatCheckbox from './checkbox';
import Dices from './dices';
import Channels, { CHAT_CHANNELS, DEFAULT_CHANNEL, isKnownChannel } from './channels';

import ChatCorner from '../../img/chat_corner.svg';
import Settings from './settings';
import SendButton from './sendButton';
import ChatInput from './input';
import { replaceIfMoreThan20 } from '../../utils/replaceIfMoreThan20';
import { loadChatPreferences, saveChatPreferences } from '../../utils/chatPreferences';

import './styles.scss';
const MAX_LENGTH = 2000; // Max message length
const TIME_LIMIT = 1; // Seconds
const SHOUT_LIMIT = 180; // Seconds
const MAX_LINES = 10;
const MAX_SHOUT_LENGTH = 100;
const MAX_HISTORY_LENGTH = 20;
const ACTIVITY_DISPLAY_DURATION_MS = 8000;

const SHOUTREGEXP = /№(.*?)№/gi;

// "/fov 100" sets the field of view without being sent as chat; bare "/fov" reports it
const FOV_COMMAND_REGEXP = /^\/fov(?:\s+(\S+))?$/i;
// "/debug" opens or closes the personal debug panel (features/personalDebug), nothing is sent as chat
const DEBUG_COMMAND_REGEXP = /^\/debug$/i;
const MIN_FOV = 50;
const MAX_FOV = 170;
const DEFAULT_FOV = 65; // the game's own value, shown while the player never chose one

const initialPrefs = loadChatPreferences();

// Accent- and case-insensitive form used to match the highlighted words
const normalizeText = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whole-word matcher for a comma-separated list ("Jean, taverne"), so "Jean" does not light up "Jeanne".
// Null when the list holds nothing usable.
function buildHighlightRegex(wordList) {
  const words = String(wordList || '')
    .split(',')
    .map((w) => normalizeText(w.trim()))
    .filter((w) => w.length > 0)
    .slice(0, 20)
    .map(escapeRegExp);
  if (words.length === 0) return null;
  try {
    return new RegExp(`(^|[^\\p{L}\\p{N}])(?:${words.join('|')})(?=$|[^\\p{L}\\p{N}])`, 'u');
  } catch (e) {
    return null;
  }
}

// FiveM-style autocomplete hint: the two catalogs the client-side
// commandCatalogService pushes (see that file's header comment) - the full
// static list once at connect, and the subset currently visible to this
// player, recomputed whenever their permissions change (faction rank,
// consoleCommandsAllowed). Only a name/usage/description match here, no
// filtering logic duplicated beyond the simple prefix check below.
function getCommandSuggestion(text, commandCatalog, visibleCommandNames) {
  if (!text.startsWith('/')) return null;
  const lower = text.toLowerCase();
  const candidates = commandCatalog.filter(
    (c) => visibleCommandNames.has(c.name) && c.name.toLowerCase().startsWith(lower)
  );
  if (candidates.length === 0) return null;
  // Shortest match first (e.g. "/bounty" before "/bounty check" while typing
  // "/bounty"), alphabetical after that for a stable order.
  candidates.sort((a, b) => a.name.length - b.name.length || a.name.localeCompare(b.name));
  return candidates[0];
}

const Chat = (props) => {
  const [input, updateInput] = useState('');
  const [commandCatalog, setCommandCatalog] = useState([]);
  const [visibleCommandNames, setVisibleCommandNames] = useState(() => new Set());
  const [isInputFocus, changeInputFocus] = useState(false);
  const [activeChannel, setActiveChannel] = useState(isKnownChannel(initialPrefs.activeChannel) ? initialPrefs.activeChannel : DEFAULT_CHANNEL);
  const [unread, setUnread] = useState({});
  const [highlightWords, setHighlightWords] = useState(typeof initialPrefs.highlightWords === 'string' ? initialPrefs.highlightWords : '');
  const highlightRegex = useMemo(() => buildHighlightRegex(highlightWords), [highlightWords]);
  const lastSeenMessage = useRef(undefined);
  // 0 = never chosen: the game's own field of view stays untouched
  const [fov, setFovState] = useState(Number.isFinite(initialPrefs.fov) ? initialPrefs.fov : 0);
  const [disableDiceSounds, setDisableDiceSounds] = useState(!!initialPrefs.disableDiceSounds);
  const [disableDiceColors, setDisableDiceColors] = useState(false);
  const [isPouchOpened, setPouchOpened] = useState(0);
  const [moveChat, setMoveChat] = useState(false);
  const [showSendButton, setSendButtonShow] = useState(!!initialPrefs.showSendButton);
  const [isSettingsOpened, setSettingsOpened] = useState(false);
  const [fontSize, setFontSize] = useState(initialPrefs.fontSize || 16);
  const placeholder = props.placeholder;
  const isInputHidden = props.isInputHidden;
  const send = props.send;
  const visibilityMode = props.visibilityMode || 'onActivity';
  const setVisibilityMode = props.setVisibilityMode;
  const [recentActivity, setRecentActivity] = useState(false);
  const activityTimeoutRef = useRef();
  const [lastSendInputText, setLastSendInputText] = useState(0);

  const [doesIncludeShout, setIncludeShout] = useState(false);

  const [shoutLength, setShoutLength] = useState(0);

  const inputRef = useRef();

  const chatRef = useRef();

  const isReset = useRef(true);

  const shoutReset = useRef(true);

  const messagesHistory = useRef([]);

  const currentMessageInHistory = useRef(-1);

  const writtenMessage = useRef('');

  const handleScroll = () => {
    if (chatRef.current) {
      window.needToScroll = (chatRef.current.scrollTop === chatRef.current.scrollHeight - chatRef.current.offsetHeight);
    }
  };

  const setEndOfContenteditable = (elem) => {
    const sel = window.getSelection();
    sel.selectAllChildren(elem);
    sel.collapseToEnd();
  };

  const addMessageToHistory = (message) => {
    messagesHistory.current = [message, ...messagesHistory.current];
    if (messagesHistory.length > MAX_HISTORY_LENGTH) {
      messagesHistory.current = messagesHistory.current.slice(0, MAX_HISTORY_LENGTH);
    }
    currentMessageInHistory.current = -1;
    writtenMessage.current = '';
  };

  const selectChannel = useCallback((id) => {
    setActiveChannel(id);
    setUnread((prev) => (prev[id] ? { ...prev, [id]: 0 } : prev));
    saveChatPreferences({ activeChannel: id });
    window.needToScroll = true;
  }, []);

  const changeFov = useCallback((value) => {
    const clamped = Math.round(Math.min(MAX_FOV, Math.max(MIN_FOV, value)));
    setFovState(clamped);
    saveChatPreferences({ fov: clamped });
    return clamped;
  }, []);

  // One grey line in the System tab, only this player sees it
  const noteToSelf = useCallback((text) => {
    window.storage?.dispatch({
      type: 'ADD_CHAT_MSG',
      data: { text: [{ text, color: '#9a9a9a', opacity: 1, type: ['italic'] }], category: 'rp', channel: 'system' },
    });
  }, []);

  const runFovCommand = useCallback((argument) => {
    if (argument === undefined) {
      noteToSelf(`Champ de vision : ${fov || DEFAULT_FOV} (/fov ${MIN_FOV}-${MAX_FOV})`);
      return;
    }
    const value = Number(argument.replace(',', '.'));
    if (!Number.isFinite(value) || value < MIN_FOV || value > MAX_FOV) {
      noteToSelf(`Champ de vision invalide : utilisez /fov suivi d'un nombre entre ${MIN_FOV} et ${MAX_FOV}.`);
      return;
    }
    noteToSelf(`Champ de vision : ${changeFov(value)}`);
  }, [fov, changeFov, noteToSelf]);

  const sendMessage = useCallback((text) => {
    if (DEBUG_COMMAND_REGEXP.test(text.trim())) {
      window.dispatchEvent(new CustomEvent('skymp5-front:togglePersonalDebug'));
      updateInput('');
      inputRef.current.innerHTML = '';
      inputRef.current.focus();
      return;
    }
    const fovCommand = text.trim().match(FOV_COMMAND_REGEXP);
    if (fovCommand) {
      runFovCommand(fovCommand[1]);
      updateInput('');
      inputRef.current.innerHTML = '';
      inputRef.current.focus();
      return;
    }
    const shout = text.match(SHOUTREGEXP);
    const shoutLen = shout
      ? shout.reduce((acc, text) => {
        acc += text.length;
        return acc;
      }, 0)
      : 0;
    if (text !== '' && text.length <= MAX_LENGTH && isReset.current && shoutLen <= MAX_SHOUT_LENGTH && (shoutLen === 0 || shoutReset.current)) {
      if (send !== undefined) {
        const message = replaceIfMoreThan20(text.trim(), '\n', '', MAX_LINES);
        // HRP tab sends global; every other tab sends local. The system tab is read-only, so a message typed there moves to Local.
        const channel = CHAT_CHANNELS.find((c) => c.id === activeChannel);
        send(message, !!channel?.sendsGlobal);
        addMessageToHistory(message);
        if (activeChannel === 'system') selectChannel('local');
      }
      isReset.current = false;
      updateInput('');
      inputRef.current.innerHTML = '';
      inputRef.current.focus();
      if (shout) {
        shoutReset.current = false;
        setTimeout(() => {
          shoutReset.current = true;
        }, 1000 * SHOUT_LIMIT);
        setIncludeShout(false);
        setShoutLength(0);
      }
    }
  }, [send, updateInput, input, isReset.current, shoutReset.current, shoutLength, doesIncludeShout, activeChannel, selectChannel, runFovCommand]);

  useEffect(() => {
    window.needToScroll = true;
    const interval = setInterval(() => {
      isReset.current = true;
    }, 1000 * TIME_LIMIT);
    return () => clearInterval(interval);
  }, []);

  // Pushed by commandCatalogService.ts: the full catalog once at connect,
  // the visible-names subset at connect and whenever it changes (faction
  // rank/admin flag). See getCommandSuggestion above for how they combine.
  useEffect(() => {
    const onCatalog = (e) => setCommandCatalog(e.detail?.commands || []);
    const onVisibility = (e) => setVisibleCommandNames(new Set(e.detail?.names || []));
    window.addEventListener('skymp5-client:commandCatalog', onCatalog);
    window.addEventListener('skymp5-client:commandVisibility', onVisibility);
    return () => {
      window.removeEventListener('skymp5-client:commandCatalog', onCatalog);
      window.removeEventListener('skymp5-client:commandVisibility', onVisibility);
    };
  }, []);

  useEffect(() => {
    const node = inputRef.current;
    const listener = (event) => {
      // Imitate message sending on Enter press. Use event.key (the logical
      // key produced — "Enter" for both main and numpad Enter) rather than
      // event.code (the physical key position, which CEF's synthetic events
      // don't always derive correctly for the numpad's extended scan code).
      if (event.key === 'Enter' && !event.shiftKey && inputRef.current) {
        event.preventDefault();
        sendMessage(input);
      }

      // Tab completes the active command suggestion (see
      // getCommandSuggestion) - same contentEditable-manipulation trio
      // (innerHTML + updateInput + setEndOfContenteditable) the history
      // recall below already uses, just completing to a command name
      // instead of a past message.
      if (event.key === 'Tab' && inputRef.current) {
        const suggestion = getCommandSuggestion(input, commandCatalog, visibleCommandNames);
        if (suggestion) {
          event.preventDefault();
          const completed = `${suggestion.name} `;
          updateInput(completed);
          inputRef.current.innerHTML = completed;
          setEndOfContenteditable(inputRef.current);
        }
      }

      // Bare Up/Down recall history (like Discord/IRC clients), Ctrl+Up/Down
      // always works as an explicit override. Guard against a message the
      // player is actively composing across multiple lines (Shift+Enter) so
      // the arrow keys still move the caret normally there instead of
      // clobbering an in-progress multi-line draft.
      const historyModeActive = currentMessageInHistory.current !== -1;
      const canRecallHistory = event.ctrlKey || historyModeActive || !input.includes('\n');

      if (event.key === 'ArrowUp' && !event.shiftKey && !event.altKey && canRecallHistory) {
        if (currentMessageInHistory.current === -1) {
          writtenMessage.current = input;
        }
        if (currentMessageInHistory.current + 1 < messagesHistory.current.length) {
          event.preventDefault();
          currentMessageInHistory.current = currentMessageInHistory.current + 1;
          updateInput(messagesHistory.current[currentMessageInHistory.current]);
          inputRef.current.innerHTML = messagesHistory.current[currentMessageInHistory.current];
          setEndOfContenteditable(inputRef.current);
        }
      }
      if (event.key === 'ArrowDown' && !event.shiftKey && !event.altKey && canRecallHistory) {
        if (currentMessageInHistory.current >= 0) {
          event.preventDefault();
          if (currentMessageInHistory.current === 0) {
            updateInput(writtenMessage.current);
            inputRef.current.innerHTML = writtenMessage.current;
            setEndOfContenteditable(inputRef.current);
            currentMessageInHistory.current = -1;
          } else {
            currentMessageInHistory.current = currentMessageInHistory.current - 1;
            updateInput(messagesHistory.current[currentMessageInHistory.current]);
            inputRef.current.innerHTML = messagesHistory.current[currentMessageInHistory.current];
            setEndOfContenteditable(inputRef.current);
          }
        }
      }
    };
    node?.addEventListener('keydown', listener);
    return () => node?.removeEventListener('keydown', listener);
  }, [inputRef.current, input, commandCatalog, visibleCommandNames]);

  useEffect(() => {
    if (inputRef !== undefined && inputRef.current !== undefined && !isInputHidden) {
      inputRef.current.focus();
    }
  }, [isInputHidden]);

  useEffect(() => {
    if (window.needToScroll) window.scrollToLastMessage();
    if (inputRef !== undefined && inputRef.current !== undefined) {
      inputRef.current.focus();
    }

    // "onActivity" mode: a new message briefly reveals the message list,
    // then it fades back out after ACTIVITY_DISPLAY_DURATION_MS unless the
    // chat is actively being used (handled separately via isInputHidden).
    if (visibilityMode === 'onActivity') {
      setRecentActivity(true);
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = setTimeout(() => {
        setRecentActivity(false);
      }, ACTIVITY_DISPLAY_DURATION_MS);
    }
  }, [props.messages]);

  // Unread count per tab: lines that arrived since the last render, filed under a tab other than the one showing.
  // The reducer keeps the same message objects and caps the list at 50, so the newest line seen is looked up by
  // identity and, when it has already left the list, every line counts as new.
  useEffect(() => {
    const list = props.messages || [];
    const previous = lastSeenMessage.current;
    lastSeenMessage.current = list.length > 0 ? list[list.length - 1] : null;
    // undefined = first run (lines already there count as seen); null = the list was empty before
    if (previous === undefined) return;
    const at = previous === null ? -1 : list.lastIndexOf(previous);
    const fresh = at === -1 ? list : list.slice(at + 1);
    if (fresh.length === 0 || activeChannel === 'all') return;
    const added = {};
    for (const msg of fresh) {
      const channel = msg.channel || 'local';
      if (channel !== activeChannel) added[channel] = (added[channel] || 0) + 1;
    }
    if (Object.keys(added).length === 0) return;
    setUnread((prev) => {
      const next = { ...prev };
      for (const [channel, n] of Object.entries(added)) next[channel] = (next[channel] || 0) + n;
      return next;
    });
  }, [props.messages]);

  // Hand the chosen field of view to the client, which writes it to the camera. Sent again a few seconds after
  // each change in case the client was not listening yet when the page loaded.
  useEffect(() => {
    if (!fov) return undefined;
    const push = () => window.skyrimPlatform?.sendMessage?.(`chat-fov:${fov}`);
    push();
    const retry = setTimeout(push, 4000);
    return () => clearTimeout(retry);
  }, [fov]);

  // A different tab holds different lines: show its newest one
  useEffect(() => {
    window.needToScroll = true;
    if (typeof window.scrollToLastMessage === 'function') window.scrollToLastMessage();
  }, [activeChannel]);

  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
  }, []);

  const showMessageList = visibilityMode === 'visible' || !isInputHidden || (visibilityMode === 'onActivity' && recentActivity);

  const handleInput = (value) => {
    updateInput(value);
    const shout = value.match(SHOUTREGEXP);
    if (shout && shout[0] !== '') {
      setIncludeShout(true);
      setShoutLength(shout.reduce((acc, text) => {
        acc += text.length;
        return acc;
      }, 0));
    } else {
      setIncludeShout(false);
      setShoutLength(0);
    }
  };

  const getMessageSpans = (message) => {
    let isNonRp = message.category === 'plain';
    const result = message.text.map(({ text, color, opacity, type }, i) => {
      if (i >= 1) {
        isNonRp = (type.includes('nonrp') && isNonRp);
      }
      return <span key={`${text}_${i}`} style={{ color: `${color}`, opacity: opacity }} className={`${type.join(' ')}`}>{text}</span>;
    });
    return [result, isNonRp];
  };

  const getList = () => {
    const visible = (props.messages || []).filter((msg) => activeChannel === 'all' || (msg.channel || 'local') === activeChannel);
    return visible.map((msg, index) => {
      const result = getMessageSpans(msg);
      // Notices from the game itself are never highlighted, only what other players say
      const isHighlighted = !!highlightRegex && msg.channel !== 'system' &&
        highlightRegex.test(normalizeText(msg.text.map((part) => part.text).join('')));
      return (
        <div
          className={`msg ${result[1] ? 'nonrp' : ''} ${isHighlighted ? 'highlight' : ''}`}
          key={`msg-${index}`}
          style={{ marginLeft: '10px', opacity: msg.opacity }}
        >
          {
            result[0]
          }
        </div>
      );
    });
  };
  return (
    <div className='fullPage'>
      <Draggable handle='#handle' disabled={!moveChat} bounds={'.fullPage'}>
        <div id='chat'>
          <div className="chat-main">
            {!isInputHidden && <Channels active={activeChannel} onSelect={selectChannel} unread={unread} />}
            <ResizableBox
              height={320}
              maxConstraints={[800, 1100]}
              minConstraints={[320, 320]}
              axis={'y'}
              style={{ display: showMessageList ? 'block' : 'none' }}
              handle={
                 !isInputHidden &&
                 <div className='chat-corner'>
                   <img src={ChatCorner} />
                 </div>
              }
              resizeHandles={['nw']}
              className='list'
              id='handle'
            >
              <div className='chat-list' style={{ fontSize }} ref={chatRef} onScroll={(e) => handleScroll()}>
                {getList()}
              </div>
            </ResizableBox>
            <div
              style={{
                height: '100px',
                display: !isInputHidden ? 'none' : 'block'
              }}
            />
            <div
              className='input'
              style={{
                display: isInputHidden ? 'none' : 'block'
              }}
            >
              {(() => {
                const suggestion = getCommandSuggestion(input, commandCatalog, visibleCommandNames);
                return suggestion && (
                  <div className='chat-command-hint'>
                    <span className='chat-command-hint--usage'>{suggestion.usage}</span>
                    <span className='chat-command-hint--desc'>{suggestion.description}</span>
                  </div>
                );
              })()}
              <div className='chat-input'>
                <ChatInput
                  id="chatInput"
                  className={'show'}
                  type="text"
                  placeholder={placeholder !== undefined ? placeholder : ''}
                  onChange={(value) => {
                    handleInput(value);
                    if (lastSendInputText + 1000 < Date.now()) {
                      window.skyrimPlatform?.sendMessage?.('onInput');
                      setLastSendInputText(Date.now());
                    }
                  }}
                  onFocus={(e) => changeInputFocus(true)}
                  onBlur={(e) => changeInputFocus(false)}
                  ref={inputRef}
                  fontSize={fontSize}
                  maxLines={MAX_LINES}
                />
                {
                  showSendButton && <SendButton onClick={() => sendMessage(input)} />
                }
              </div>
              <div className='chat-checkboxes'>
                <ChatCheckbox
                  id={'settings'}
                  text={'Paramètres'}
                  isChecked={isSettingsOpened}
                  onChange={(e) => {
                    inputRef.current.focus();
                    setSettingsOpened(e.target.checked);
                  }} />
                {/* Maybe we will need it later: <ChatCheckbox id={'diceColor'} text={'dice colors'} isChecked={!disableDiceColors} onChange={(e) => setDisableDiceColors(!e.target.checked)} /> */}
                <ChatCheckbox
                  id={'moveChat'}
                  text={'Déplacer le chat'}
                  isChecked={moveChat}
                  onChange={(e) => {
                    inputRef.current.focus();
                    setMoveChat(e.target.checked);
                  }} />
                { doesIncludeShout &&
                  <span className={`chat-message-limit shout-limit ${shoutLength > MAX_SHOUT_LENGTH ? 'limit' : ''} text`}>{shoutLength}/{MAX_SHOUT_LENGTH}</span>
                }
                {/* <span className={`chat-message-limit ${input.length > MAX_LENGTH ? 'limit' : ''} text`}>{input.length}/{MAX_LENGTH}</span> */}
              </div>
            </div>
          </div>
          {
            isInputHidden
              ? <></>
              : <Dices
                  isOpened={isPouchOpened}
                  setOpened={setPouchOpened}
                  send={props.send}
                  disableSound={disableDiceSounds}
                  inputRef={inputRef}
              />
          }
        </div>
      </Draggable>
      {
        (isSettingsOpened && !isInputHidden) &&
        <Settings
          fontSize={fontSize}
          setFontSize={(value) => { setFontSize(value); saveChatPreferences({ fontSize: value }); }}
          isSoundsDisabled={disableDiceSounds}
          setDisableSounds={(disabled) => { setDisableDiceSounds(disabled); saveChatPreferences({ disableDiceSounds: disabled }); }}
          showSendButton={showSendButton}
          setShowSendButton={(value) => { setSendButtonShow(value); saveChatPreferences({ showSendButton: value }); }}
          visibilityMode={visibilityMode}
          setVisibilityMode={setVisibilityMode}
          fov={fov || DEFAULT_FOV}
          minFov={MIN_FOV}
          maxFov={MAX_FOV}
          setFov={changeFov}
          highlightWords={highlightWords}
          setHighlightWords={(value) => { setHighlightWords(value); saveChatPreferences({ highlightWords: value }); }}
        />
      }
    </div>
  );
};

export default Chat;
